<template lang="pug">
#app-content(
  :class="courseCssClasses"
  :contenteditable="wordDoc ? 'true' : null"
  :data-audio-doc="audioDoc ? 'true' : null"
  :data-theme="content?.course?.theme"
  :spellcheck="wordDoc ? 'true' : null"
  v-if="dataReady"
)
  img(
    :src="firm.match(/[a-zA-Z]/) ? `screenshots/${firm}_${courseId}.png` : `screenshots/${courseId}.png`"
    onerror="this.style.display='none'"
    style="display: inline !important"
    uc-word-only=""
    v-if="wordDoc"
  )
  uc-edit-bar(v-if="portalReady")

  uc-nav-menu(
    uc-word-skip=""   
    :bslEnabled="isBslEnabled" 
    @update:bslEnabled="toggleBsl"
  )

  .fixed.left-20.bottom-0.z-20
    uc-language-widget(
      uc-word-skip=""    
    )

  .fixed.left-3.bottom-3.z-10.bottom-left-logo.hidden.xl_block.rtl_left-auto.rtl_right-3
    uc-logo.inline-block.h-auto(:class=`showCustomLogo || hasCustomGlobalLogo ? 'w-[8em]' : 'w-[3em]'` v-if="!content.languages" :showLogoIcon="true" ref="logoRef")

  //- transition-fade(:delay="5" :duration="1")
  //-   uc-nav-menu 

  //- transition-fade(:delay="6" :duration="1")
  //-   uc-mute-button

  .app-template
    .pages
      router-link#not-found.text-fuchsia-300.block.fixed.inset-0.m-auto.flex.flex-col.items-center.justify-center.text-center.w-full.opacity-80.hover_opacity-100.transition.border-4.rounded-xl(
        :class="'max-w-[20em] max-h-[20em] hover_bg-fuchsia-900/20'"
        :to="`/${firstPage.id}`"
        aria-hidden="true"
        v-if="!wordDoc"
      )
        uc-icon.text-9xl.mb-8(icon="mdi:glass-fragile")
        p.mb-2 This page looks broken.
        p Return home?

      uc-audio-popup(:firstPage="firstPage" v-if="!audioDoc && !wordDoc")

      uc-filter-tags(v-slot="{ filterTags }")
        uc-page.bg-gray-800(
          :class="page.style"
          :key="page.id"
          :page="page"
          v-for="page in filterTags(content?.pages)"
        )
          template(v-if="page.bg")
            uc-asset.h-screen.w-screen.fixed.top-0.left-0.object-cover.max-w-none.rtl_-scale-x-100(
              :data-page-background="page.id"
              :src="src"
              aria-hidden="true"
              autoplay="true"
              v-for="src in getPageBg(page)"
            )

          .parallax-bg-wrapper(
            :data-page-parallax="page.id"
            v-if="page.parallax"
          )
            .parallax-bg.h-screen.w-screen.top-0.left-0.rtl_-scale-x-100
              img.h-screen.w-screen.fixed.object-cover.max-w-none(
                :data-page-background="page.id"
                :data-page-parallax-background="page.id"
                :src="page.parallax"
                aria-hidden="true"
              )
          uc-scroll-page(
            :data-section-hidden="!isEditMode && isHidden(page.id) ? true : null"
            :type="page.type"
          )
            template(
              :key="screen.id"
              v-for="screen in filterTags(page.screens)"
            )
              uc-scroll-section(
                :data-audio="screen.audio || screen.speech || null"
                :data-pass-course="screen['pass-course']"
                :data-section-hidden="!isEditMode && isHidden(screen.id) ? true : null"
                :id="screen.id"
              )
                audio-marker(:name="screen.id")
                uc-screen.pt-edit-bar(
                  :class="[{ 'saturate-0 screen--no-fade': editModeAvailable && isEditMode && isHidden(screen.id) }, { '!hidden': !isEditMode && isHidden(screen.id) }]"
                  :editPath="`pages.${page.pageIndex}.screens.${screen.screenIndex}`"
                  :screen="screen"
                  :show-close-button="showCloseButton"
                )
                  uc-course-complete(
                    v-if="screen['pass-course'] && showCloseButton && !wordDoc && !audioDoc"
                  )
                uc-dynamic-global-pill(
                  :class="{ dark: screen.dark }"
                  :id="screen.id"
                  :screen="screen"
                  :unit="page"
                  :hasDelete="canDeleteScreen(page, screen)"
                  type="section"
                  v-if="isEditMode && hasTagDescriptions && canToggleScreen(page, screen)"
                )

                //- toggle, but not first or last screen
                uc-toggle-included(
                  :class="{ dark: screen.dark }"
                  :id="screen.id"
                  :screen="screen"
                  :unit="page"
                  :hasDelete="canDeleteScreen(page, screen)"
                  type="section"
                  v-if="isEditMode && canToggleScreen(page, screen)"
                )

                //- delete, but only client added pages
                //- need to offset left uc-dynanic-global-pill and uc-toggle-included is this is displayed
                uc-delete-included(
                  :class="{ dark: screen.dark }"
                  :id="screen.id"
                  :screen="screen"
                  :unit="page"
                  type="section"
                  v-if="isEditMode && canDeleteScreen(page, screen)"
                )

                br.hidden(uc-word-only="")

              //- Word/Audio doc end-section teleport target for any lightbox content that is not in a nested 'templates:' screen (HSG has it's own teleport targets after each level)
              .word-audio-section-lightbox-target(:data-teleport="screen.id" v-if="(wordDoc || audioDoc) && !screen?.templates && screen?.template !== 'hotspot' && screen?.template !== 'hotspot-game' && screen?.template !== 'hotspot-fullscreen'")

  //- adds screen after current screen
  uc-add-screen(
    type="section"
    v-if="isEditMode && addPageEnabled"
  )

  .kss.theme-skillboosters.fixed.z-1000.bottom-2.right-6.text-xs.flex.gap-2.p-1.items-center.rounded(
    :class="'bg-gray-500/0'"
    v-if="isKitchenSink || mode === 'design'"
  )
    uc-theme-select(v-if="isKitchenSink")
    uc-primary-select
    uc-primary-select(level="secondary")
    uc-primary-select(level="gray")

  #uc-lightbox-portal.fixed.items-center.justify-center.inset-0.z-50.pointer-events-none
    uc-lightbox#lightbox-upload-logo.primary-sky(v-if="editModeAvailable && !wordDoc && !audioDoc")
      uc-upload-logo

    uc-lightbox#lightbox-upload-image.primary-sky(v-if="editModeAvailable && !wordDoc && !audioDoc")
      uc-upload-image

    uc-lightbox#lightbox-inlinelink.w-screen(:block="true")
      .flex.flex-col.h-full
        .py-2.flex-none
          uc-icon.me-1.text-2xl.text-primary-500(icon="mdi:web")
          uc-text.font-bold.font-mono.text-base(:text="currentInlineLink")
        .flex-1.relative
          iframe.block.top-0.mx-auto.w-full.h-full.border.border-gray-200.rounded(
            :src="currentInlineLink"
            height="100%"
            v-if="currentInlineLink"
            width="100%"
          ) 
          .failed-container(v-if="!currentInlineLink && !wordDoc && !audioDoc")
            uc-text(text="Failed to fetch URL")
      template(v-slot:buttons="{ close }")
        uc-button#close.btn.btn-primary.mx-2.px-1(@click="close" text="Close")

  #tooltip-container.fixed.inset-0.z-60.pointer-events-none.overflow-hidden(
    v-if="content.tooltips"
  )
    uc-tooltip

  video.bsl_video.fixed.z-50.fixed.top-2.right-6.opacity-95.aspect-square.object-cover.rounded-full.bg-gray-300.shadow-lg.w-48.md_w-64(
    :class="isGrabbing ? 'cursor-grabbing' : 'cursor-grab'"
    v-if="bslURL && isBslEnabled && bslVideoSrc"
    ref="bslEl"
    allow="autoplay;"
    autoplay
    muted
    playsinline
    webkit-playsinline
    preload="auto"
    :style="bslStyle"
    :src="bslVideoSrc"
    type="video/mp4"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
  )
</template>

<script setup lang="ts">
import {
  computed,
  watchEffect,
  ref,
  provide,
  inject,
  unref,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  defineAsyncComponent,
  nextTick,
  watch,
  onErrorCaptured
} from 'vue';

import { primaries, secondaries, grays } from './lib/colorClasses.yml';
import useSuspend from './lib/useSuspend';
import SCORM, {
  getCourseId,
  getFirm,
  setTags,
  setExit,
  isFirm
} from './lib/SCORM';
import useContent from './stores/useContent';
import usePreloadImages from './lib/usePreloadImages';
import useFavicon from './lib/useFavicon';
import { storeToRefs } from 'pinia';
import useEditMode from './stores/useEditMode';
import bugAlert from './lib/bugAlert';
import {
  useEventListener,
  breakpointsTailwind,
  useBreakpoints,
  useDraggable
} from '@vueuse/core';
import isVisible from './lib/isVisible';
import useTheme from './stores/useTheme';
import { stop } from './lib/audio';
import useFilterTags from './lib/useFilterTags';
import { playSfx } from './lib/sfx';
import openLightbox from './lib/openLightbox';
import { enable as enableAudio, disable as disableAudio } from './lib/audio';
import getQueryParameter from './lib/getQueryParameter';
import removeMarkdown from '../esm/lib/removeMarkdown.js';
import isHidden from './lib/isHidden';
import getContentBase from './lib/getContentBase';
import { hasTagDescriptions } from './lib/getTags';
import useAudioNarration from './lib/useAudioNarration';
import useJitAudioLookahead from './lib/useJitAudioLookahead';
import useJitAuthorApi from './lib/useJitAuthorApi';

onErrorCaptured(err => {
  bugAlert(err);

  return false;
});

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));

useJitAudioLookahead({
  enabled: () => !audioDoc && !wordDoc
});

// Internal Authoring Tool JiT bridge: expose `window.ucJit` so AuthorAudioJIT
// (running this runtime in a same-origin iframe) can enumerate/regenerate slots
// without re-implementing course loading. Never loads for learners.
if (getQueryParameter('ucAuthorAudio')) {
  useJitAuthorApi();
}

const firm = getFirm();
const courseId = getCourseId();
const unlockAllActivated = ref(false);
const currentInlineLink = ref();
const logoRef = ref<HTMLElement | null>(null);
const base = getContentBase();
const firmTerm =
  /^[a-zA-Z]/.test(firm) && !isFirm('REVIEW') ? firm.toLowerCase() : null;

const hasSessionEnded = ref(false);
const showCloseButton = computed(() => {
  if (typeof content.value.course['close-course'] === 'boolean') {
    return content.value.course['close-course'];
  }
  return true;
});
useTheme();

if (import.meta.env.DEV) {
  /**
   * alt+U to unlock all content
   */
  const unlockListener = (e: KeyboardEvent) => {
    if (e.altKey && e.code === 'KeyU') {
      unlockAllActivated.value = true;
      setTags();
    }
  };

  useEventListener(window, 'keydown', unlockListener);

  /**
   * alt+] and alt+[ to for RTL and LTR
   */
  const rtlListener = (e: KeyboardEvent) => {
    if (e.altKey && e.code === 'BracketRight') {
      document.documentElement.dir = 'rtl';
    }

    if (e.altKey && e.code === 'BracketLeft') {
      document.documentElement.dir = 'ltr';
    }
  };

  useEventListener(window, 'keydown', rtlListener);

  /**
   * alt+1 and alt+0 to answer questions
   * TODO: add support for other question templates (grid, slider, ordering, etc.)
   */
  const cheatCodesHandler = async (e: KeyboardEvent) => {
    const forms = document.querySelectorAll(
      'form.question:not(.-is-submitted)'
    );

    const focusedForm = document.querySelector(
      'form.question:not(.-is-submitted):focus-within'
    );

    const firstVisibleForm = [...forms].filter(isVisible)[0];

    const form = focusedForm || firstVisibleForm;

    if (!form) return;

    if (e.altKey && ['Digit0', 'Digit1'].includes(e.code)) {
      const toSelect = [...form.querySelectorAll('[value="1"]')].map(
        input => input.id
      );

      if (e.code === 'Digit0') {
        toSelect.pop();
        toSelect.push(form.querySelector('[value="0"]')?.id as string);
      }

      toSelect.forEach(id => {
        const input = document.getElementById(id) as HTMLInputElement;
        if (input && !input.checked) input.click();
      });

      await nextTick();

      form.querySelector('button.question__submit')?.click();
    }
  };

  useEventListener(window, 'keydown', cheatCodesHandler);
}

const suspend = useSuspend();

// storeToRefs for reactivity: https://pinia.vuejs.org/core-concepts/#using-the-store
const { compiled: content } = storeToRefs(useContent());
const findAdvancedPatchScreenById = useContent().findAdvancedPatchScreenById;

if (content.value.course.edit) {
  useEditMode().available = true;
}

provide('content', content);
provide(
  'moreButtons',
  computed(() => content.value.course['more-buttons'])
);

const {
  available: editModeAvailable,
  addPageEnabled,
  mode
} = storeToRefs(useEditMode());
const isEditMode = computed(() => mode.value === 'edit');

const canToggleScreen = (page: Page, screen: UcScreen) => {
  const mandatoryTemplate = [
    'course-title',
    'unit-title',
    'unit-end',
    'dashboard',
    'assessment',
    'bank',
    'quiz',
    'result',
    'builder'
  ].includes(screen?.template);

  // don't let the 'pass-course' screen be hidden in edit mode
  const isPassCourse = screen?.['pass-course'] === true;

  return !mandatoryTemplate && !isPassCourse;
};

const canDeleteScreen = (page: Page, screen: UcScreen) => {
  const isCustomisation = findAdvancedPatchScreenById(screen.id);
  return isCustomisation !== null;
};

const langParam = getQueryParameter('lang');
const languages = content.value.languages || [];
const navLang = window.navigator.language;

if (langParam) {
  SCORM.set('locale', langParam);
} else if (languages.includes(navLang)) {
  SCORM.set('locale', navLang);
}

const breakpoints = useBreakpoints(breakpointsTailwind);
provide(
  'largerThanSm',
  computed(() => breakpoints.greater('sm'))
);
const smallDevice = breakpoints.smaller('md');
const smallerThanLg = breakpoints.smaller('lg');

const getPageBg = (page: Page) => {
  if (page?.['bg-small'] && smallDevice.value) {
    return page?.['bg-small']?.split(/ +/);
  } else if (page?.['bg-medium'] && smallerThanLg.value) {
    return page?.['bg-medium']?.split(/ +/);
  } else {
    return page?.bg?.split(/ +/);
  }
};

onMounted(() => {
  const w =
    import.meta.env.DEV && window.top instanceof Window ? window.top : window;
  w.document.title = removeMarkdown(
    removeLinebreaks(content.value.course.title.trim())
  );
});

// don't preload images when creating audio scripts
const firstImageLoaded =
  audioDoc || wordDoc ? ref(true) : usePreloadImages(content.value);

const isKitchenSink = computed(() => getCourseId() === 0);
// const isKitchenSink = false;

const unlockAll = computed(() => {
  return (
    isKitchenSink.value ||
    unlockAllActivated.value ||
    mode.value !== 'view' ||
    audioDoc ||
    wordDoc
  );
});

const filterHidden = items => {
  return items.filter(item => !isHidden(item.id));
};

const removeLinebreaks = text => {
  return text
    .replace(/ *\\?\n/g, ' ')
    .replace(/{r}/g, ' ')
    .replace(/{n}/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ');
};

const filterTags = useFilterTags();

const units = computed(() => {
  const allUnits = content.value?.pages?.filter(p => p.type === 'unit');
  const filterTaggedUnits = filterTags(allUnits);
  const filterHiddenUnits = filterHidden(filterTaggedUnits);
  return (isEditMode.value ? filterTaggedUnits : filterHiddenUnits).map(
    (u, i) => ({ ...u, number: i + 1 })
  );
});

const bslURL = computed(() => content.value.course.audio?.bsl ?? false);
const isBslEnabled = ref(true);
const bslEl = ref<HTMLElement | null>(null);
const { style: bslStyle } = useDraggable(bslEl, {
  initialValue: { x: 'calc(100% - 400px - 1rem)', y: '1rem' }
});

function toggleBsl(newValue) {
  isBslEnabled.value = newValue;
}

const bslVideoSrc = ref('');

const updateBslVideoSrc = event => {
  const audioURL = event.detail.activeId;
  const filename = audioURL.split('/').pop().replace('.mp3', '.mp4');
  const path = bslURL.value?.toString().endsWith('/')
    ? bslURL.value
    : bslURL.value + '/';
  bslVideoSrc.value = path + filename;
};

const isGrabbing = ref(false);

const handleMouseDown = () => {
  isGrabbing.value = true;
};

const handleMouseUp = () => {
  isGrabbing.value = false;
};

onMounted(() => {
  document.addEventListener('activeAudioChanged', updateBslVideoSrc);
});

onUnmounted(() => {
  document.removeEventListener('activeAudioChanged', updateBslVideoSrc);
});

onMounted(() => {
  window.addEventListener('mouseup', handleMouseUp);
});

onBeforeUnmount(() => {
  window.removeEventListener('mouseup', handleMouseUp);
});

onMounted(() => {
  window.addEventListener('resize', () => {
    keepInWindow();
  });
});

function keepInWindow() {
  if (!bslEl.value) return;

  const { x, y } = bslEl.value.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;

  if (x + bslEl.value.offsetWidth > innerWidth) {
    bslEl.value.style.left = `${innerWidth - bslEl.value.offsetWidth - 10}px`;
  }

  if (y + bslEl.value.offsetHeight > innerHeight) {
    bslEl.value.style.top = `${innerHeight - bslEl.value.offsetHeight - 10}px`;
  }
}

const firstPage = computed(() => {
  return content.value?.pages[0];
});

const finalPage = computed(() => {
  return content.value?.pages?.filter(p => p.type === 'final')[0];
});

const unitsAndTest = computed(() => {
  const test = content.value?.pages?.find(p => p.type === 'final');

  return [...units.value, test].filter(Boolean);
});

// dev error overlay for missing `type: unit`
watchEffect(() => {
  if (!unitsAndTest.value[0]) {
    bugAlert('The course has no pages with `type: unit` or `type: final`.');
  }
});

const unitCount = computed(() => unitsAndTest.value.length);

const completedCount = computed(
  () => unitsAndTest.value.filter(page => suspend.completed[page.id]).length
);

if (wordDoc) {
  onMounted(() => {
    setTimeout(() => {
      try {
        (
          document.querySelector('.-word[contenteditable]') as HTMLInputElement
        )?.focus();
        document.execCommand('selectAll', false, null);
        document.getSelection()?.collapseToStart();
      } catch (e) {
        //
      }
    }, 200);
  });
}

provide(
  'theme',
  computed(() => content.value.course.theme)
);

const unlockScreens = computed(
  () =>
    unlockAll.value ||
    ['screens', 'course'].includes(content.value.course.unlock)
);

const unlockUnits = computed(
  () =>
    unlockAll.value || ['units', 'course'].includes(content.value.course.unlock)
);

const unlockCourse = computed(
  () => unlockAll.value || ['course'].includes(content.value.course.unlock)
);

const hasSfx = computed(() =>
  JSON.stringify(content.value || '').includes('"sfx":')
);

const { audioAiJitEnabled } = useAudioNarration();

const hasAudio = computed(
  () =>
    content.value.course?.audio?.[suspend.locale] ||
    audioAiJitEnabled.value ||
    JSON.stringify(content.value.pages || '').includes('"audio":')
);

const hasSpeech = computed(() =>
  JSON.stringify(content.value || '').includes('"speech":')
);

const hasVideo = computed(() => {
  const jsonString = JSON.stringify(content.value || '');
  return (
    jsonString.includes('"template":"video"') ||
    jsonString.includes('"template":"lightbox-video"') ||
    jsonString.includes('"video":')
  );
});

const hasVoicemail = computed(() => {
  const jsonString = JSON.stringify(content.value || '');
  return (
    jsonString.includes('"template":"voicemail"') ||
    jsonString.includes('"template":"guided-voicemail"') ||
    jsonString.includes('"template":"voicemail-question"')
  );
});

const isAudioLocked = computed(() => {
  return content.value.course.audiolocked;
});

// enable/disable audio in the course
watchEffect(() => {
  if (
    hasAudio.value ||
    hasSpeech.value ||
    hasVoicemail.value ||
    hasVideo.value
  ) {
    enableAudio();
  } else if (
    !hasAudio.value &&
    !hasSpeech.value &&
    !hasVoicemail.value &&
    !hasVideo.value
  ) {
    disableAudio();
  }
});

provide('isKitchenSink', isKitchenSink);
provide('unlockAll', unlockAll);
provide('unlockScreens', unlockScreens);
provide('unlockUnits', unlockUnits);
provide('unlockCourse', unlockCourse);
provide('units', units);
provide('unitsAndTest', unitsAndTest);
provide('completedCount', completedCount);
provide('hasSfx', hasSfx);
provide('hasAudio', hasAudio);
provide('hasSpeech', hasSpeech);
provide('hasBsl', bslURL);
provide('hasVoicemail', hasVoicemail);
provide('hasVideo', hasVideo);
// provide('unitCount', unitCount);

provide(
  'finalUnlocked',
  computed(() => units.value.every(p => suspend.completed[p.id]))
);

provide(
  'hasCaptions',
  computed(() => JSON.stringify(content.value || '').includes('"captions":'))
);

provide('isAudioLocked', isAudioLocked);

// expose data to $parent chain for uc-text placeholders
defineExpose({
  isKitchenSink,
  unlockAll,
  units,
  unitsAndTest,
  unitCount,
  completedCount
});

provide(
  'nextUnits',
  computed(() => {
    const map = {};
    // we don't include hidden units when checking for the next one
    const order = filterHidden(units.value);

    order.forEach((u, i) => {
      if (i < order.length - 1) {
        map[u.id] = order[i + 1];
      }

      if (i === order.length - 1) {
        map[u.id] = finalPage.value;
      }
    });

    return map;
  })
);

useFavicon(content.value);

const dataReady = computed(() => {
  return content.value.pages?.length > 0 && firstImageLoaded.value;
});

const portalReady = ref(false);

const sfxOnClick = (e: MouseEvent) => {
  if (!hasSfx.value) return;

  const target = e.target as HTMLElement;
  const isButton =
    target.tagName.toLowerCase() === 'button' || target.closest('button');
  const sfxEl = target.closest<HTMLElement>('[data-sfx]');
  // if the user is clicking on a button set default sfx to 'continue'
  const sfx = sfxEl?.dataset.sfx
    ? sfxEl.dataset.sfx
    : isButton
      ? 'continue'
      : null;
  if (sfx) playSfx(sfx);
};

const openIframes = computed(() => content.value.course['open-iframes']);
provide('open-iframes', openIframes);

const openLinkLightbox = (e: MouseEvent) => {
  const link = (e.target as HTMLElement).closest('a');
  const iFrameEls = (e.target as HTMLElement).closest('[data-open-iframes]');
  const localiFrame = iFrameEls
    ? iFrameEls?.getAttribute('data-open-iframes') === 'true'
    : openIframes.value;

  const href = link?.getAttribute('href');
  if (!href) return;

  if (href.startsWith('#lightbox')) {
    e.preventDefault();
    openLightbox(href.slice(1));
    return;
  }

  const openIframe =
    // page or screen open-iframe prop
    localiFrame &&
    // option is turned on
    openIframes.value &&
    // external link
    href.startsWith('http') &&
    // not a download
    !href.match(/\.(xlsx|docx|xls|doc)\b/);

  if (openIframe) {
    e.preventDefault();
    const link = href.slice(0);
    currentInlineLink.value = link;
    openLightbox('lightbox-inlinelink');
  }
};

useEventListener(window, 'click', sfxOnClick);
useEventListener(window, 'click', openLinkLightbox);

watchEffect(
  () => {
    if (dataReady.value) {
      // gsap.to(
      //   '.app-template',
      //   { opacity: 1, duration: 1, ease: 'linear' }
      // );

      nextTick(() => {
        portalReady.value = true;
      });
    }
  },
  // init tween after the v-if is rendered
  { flush: 'post' }
);

watch(
  () => suspend.locale,
  () => {
    stop();
  }
);

const UcThemeSelect = defineAsyncComponent(
  isKitchenSink.value ? () => import('./lib/uc-theme-select.vue') : () => null
);

const courseCssClasses = computed(() => [
  wordDoc && '-word',
  `theme-${content.value.course.theme}`,
  primaries[content.value.course.primary],
  secondaries[content.value.course.secondary],
  grays[content.value.course.gray],
  {
    '-hide-captions': !suspend.captions,
    '-hide-transcript': !suspend.transcript,
    '-edit-mode-available': editModeAvailable.value,
    '-edit-mode-enabled': mode.value === 'edit',
    '-authoring-mode': import.meta.env.MODE?.includes?.('authoring'),
    'hyphens-auto': suspend.locale !== 'en-GB'
  }
]);

provide('courseCssClasses', courseCssClasses);

//- TODO: Below logo code is from uc-logo. It should be DRY-er.

const logo = computed(
  () =>
    content.value.course['logo-light'] ||
    content.value.course['logo-dark'] ||
    content.value.course.logo
);

const showCustomLogo = computed(
  () => logo.value !== '' && logo.value !== true && logo.value !== undefined
);

const customGlobalLogoURL = ref('');
const hasCustomGlobalLogo = ref(false);

const setGlobalLogo = url => {
  // override default course logo prop (true/undefined) so we don't use the VW logo
  const { content: courseContent } = storeToRefs(useContent());
  courseContent.value.course.logo = '';
  customGlobalLogoURL.value = url;
  hasCustomGlobalLogo.value = true;
};

const setGlobalCustomLogo = async () => {
  const logoBase = `${base}/custom/${firmTerm}`;

  const logoUrls = [
    `${logoBase}/${firmTerm}-global-logo.png`,
    `${logoBase}/${firmTerm}-global-logo.svg`,
    `${logoBase}/${firmTerm}-global-logo-light.png`,
    `${logoBase}/${firmTerm}-global-logo-light.svg`,
    `${logoBase}/${firmTerm}-global-logo-dark.png`,
    `${logoBase}/${firmTerm}-global-logo-dark.svg`
  ];

  let logoFound = false;
  for (const url of logoUrls) {
    if (logoFound) break;
    await fetch(url, { method: 'HEAD' })
      .then(r => {
        if (r && r.ok) {
          setGlobalLogo(url);
          logoFound = true;
        }
      })
      .catch(() => '');
  }
};

// if there's no course logo prop in the yaml, look for a global logo on S3 (try SVG then PNG)
if (firmTerm && !showCustomLogo.value && !wordDoc && !audioDoc) {
  setGlobalCustomLogo();
}

provide('customGlobalLogoURL', customGlobalLogoURL);

provide('showCloseButton', showCloseButton);
</script>
